// Today's Changes pane, on the runner's last report in the session feed.
// A diff is read on opening and again for each new report while it is open.
// Its file rows and their diffs serve a proposal's card on the Board too
// (board/ReadyCard.tsx), on the proposal's kept copy.
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { ApiError, errorMessage } from '../api/client'
import type { ChangedFile, Session, SessionChanges, SessionChangesDiff } from '../api/types'
import { uuid4 } from '../api/uuid'
import { ChevronDownIcon, ChevronRightIcon } from '../components/Icons'
import { exactTime } from '../components/Time'
import { needsServer } from '../components/needsServer'
import { Working } from '../components/Working'
import { useSessionsEnv } from '../sessions/context'
import { useBoardState, useStore } from '../store/context'
import { cx } from '../transcript/bits'
import { parseDiff, type ParsedDiff } from './diff'
import { DiffRows } from './DiffRows'
import './changes.css'

export const FOLD_ROWS = 400
const STATUS_WORDS = { A: 'added', M: 'modified', D: 'deleted', R: 'renamed', '?': 'untracked' }

export interface DiffState {
  at: string
  loading: boolean
  parsed: ParsedDiff | null
  error: string | null
}

/** One file's diff; past `foldRows` rows (a parameter for the tests), the rest wait behind a button. */
export function FileDiff({ file, state, unfolded, onUnfold, onRetry, foldRows = FOLD_ROWS }: {
  file: ChangedFile
  state: DiffState | undefined
  unfolded: boolean
  onUnfold(): void
  onRetry(): void
  foldRows?: number
}) {
  if (file.binary) return <p className="changes-diff-said">A binary file: there are no lines to show.</p>
  if (!file.has_diff) return <p className="changes-diff-said">The diff is too large to keep.</p>
  if (!state || (state.loading && !state.parsed)) {
    return <p className="changes-diff-said" aria-busy="true"><Working label="Loading the diff" /></p>
  }
  if (state.error !== null && !state.parsed) {
    return (
      <p className="changes-diff-said" role="alert">
        The diff could not be read: {state.error}{' '}
        <button type="button" className="button button-small" onClick={onRetry}>Try again</button>
      </p>
    )
  }
  const parsed = state.parsed!
  if (parsed.binary) return <p className="changes-diff-said">A binary file: there are no lines to show.</p>
  if (parsed.rows.length === 0) return <p className="changes-diff-said">No line of this file changed.</p>
  const folded = !unfolded && parsed.rows.length > foldRows
  const rows = folded ? parsed.rows.slice(0, foldRows) : parsed.rows
  const more = parsed.rows.length - rows.length
  return (
    <>
      <div className="changes-diff-scroll"><DiffRows rows={rows} label={`Diff of ${file.path}`} /></div>
      {folded && <button type="button" className="changes-diff-more" onClick={onUnfold}>Show the other {more.toLocaleString('en-US')} {more === 1 ? 'line' : 'lines'}</button>}
      {state.error !== null && <p className="changes-diff-said" role="alert">It could not be read again: {state.error}</p>}
    </>
  )
}

/** Which files are open, unfolded, and their diffs as last read: what useFileDiffs answers and ChangedFiles draws. */
export interface FileDiffs {
  open: ReadonlySet<string>
  toggle(path: string): void
  diffs: ReadonlyMap<string, DiffState>
  unfolded: ReadonlySet<string>
  unfold(path: string): void
  retry(path: string): void
}

/**
 * Each open file's diff, read with `read` on opening, and again when the
 * report's `at` changes while it is open. A file the report no longer lists
 * closes. An answer to a request since replaced, or for an older report, is
 * dropped.
 */
export function useFileDiffs(changes: SessionChanges | null, read: (path: string) => Promise<SessionChangesDiff>, initiallyOpen: readonly string[] = []): FileDiffs {
  const store = useStore()
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set(initiallyOpen))
  const [unfolded, setUnfolded] = useState<ReadonlySet<string>>(() => new Set())
  const [diffs, setDiffs] = useState<ReadonlyMap<string, DiffState>>(() => new Map())
  const latest = useRef({ changes, diffs })
  latest.current = { changes, diffs }
  const live = useRef(true)
  const requests = useRef(new Map<string, object>())
  useEffect(() => {
    live.current = true
    const pending = requests.current
    return () => { live.current = false; pending.clear() }
  }, [])

  const readDiff = useCallback((path: string, at: string) => {
    const request = {}
    requests.current.set(path, request)
    setDiffs((all) => new Map(all).set(path, { at, loading: true, parsed: all.get(path)?.parsed ?? null, error: null }))
    read(path).then(
      (answer) => {
        if (!live.current || requests.current.get(path) !== request || latest.current.changes?.at !== at) return
        setDiffs((all) => new Map(all).set(path, { at, loading: false, parsed: parseDiff(answer.diff), error: null }))
      },
      (err: unknown) => {
        if (!live.current || requests.current.get(path) !== request || latest.current.changes?.at !== at) return
        setDiffs((all) => new Map(all).set(path, { at, loading: false, parsed: all.get(path)?.parsed ?? null, error: errorMessage(err) }))
        if (err instanceof ApiError && err.status === 401) void store.whenSessionKnown()
      },
    )
  }, [read, store])

  const at = changes?.at
  useEffect(() => {
    const report = latest.current.changes
    const paths = new Set(report?.files.map((file) => file.path))
    if ([...open].some((path) => !paths.has(path))) setOpen(new Set([...open].filter((path) => paths.has(path))))
    for (const file of report?.files ?? []) {
      if (!open.has(file.path) || file.binary || !file.has_diff) continue
      if (latest.current.diffs.get(file.path)?.at === report?.at) continue
      readDiff(file.path, report!.at)
    }
  }, [open, at, readDiff])

  return {
    open,
    toggle: (path) => setOpen((all) => {
      const next = new Set(all)
      if (!next.delete(path)) next.add(path)
      return next
    }),
    diffs,
    unfolded,
    unfold: (path) => setUnfolded((all) => new Set(all).add(path)),
    retry: (path) => { if (latest.current.changes) readDiff(path, latest.current.changes.at) },
  }
}

/** The files a report lists, each a row with its status and counts, which opens its diff below it. */
export function ChangedFiles({ files, view }: { files: readonly ChangedFile[]; view: FileDiffs }) {
  const ids = useId()
  return (
    <ul className="changes-list">
      {files.map((file, i) => {
        const isOpen = view.open.has(file.path)
        const slash = file.path.lastIndexOf('/')
        const word = STATUS_WORDS[file.status]
        const diffId = `${ids}diff-${i}`
        return (
          <li key={file.path} className={cx('changes-file', isOpen && 'is-open')}>
            <button type="button" className="changes-file-row" aria-expanded={isOpen} aria-controls={isOpen ? diffId : undefined} title={file.old_path ? `${file.old_path} → ${file.path}` : file.path} onClick={() => view.toggle(file.path)}>
              {isOpen ? <ChevronDownIcon size={12} /> : <ChevronRightIcon size={12} />}
              <span className={cx('changes-status', `changes-status-${word}`)} aria-hidden="true">{file.status}</span>
              <span className="visually-hidden">{file.old_path ? `${word} from ${file.old_path}: ` : `${word} `}</span>
              <span className="changes-path">{slash >= 0 && <span className="changes-dir">{file.path.slice(0, slash + 1)}</span>}<span className="changes-name">{file.path.slice(slash + 1)}</span></span>
              <span className="changes-counts">{file.binary ? <span className="changes-binary">binary</span> : <>{file.additions !== null && <span className="changes-added">+{file.additions}</span>}{' '}{file.deletions !== null && <span className="changes-removed">−{file.deletions}</span>}</>}</span>
            </button>
            {isOpen && <div id={diffId} className="changes-diff"><FileDiff file={file} state={view.diffs.get(file.path)} unfolded={view.unfolded.has(file.path)} onUnfold={() => view.unfold(file.path)} onRetry={() => view.retry(file.path)} /></div>}
          </li>
        )
      })}
    </ul>
  )
}

function SessionChanges({ session }: { session: Session }) {
  const { api } = useSessionsEnv()
  const store = useStore()
  const { me, offline } = useBoardState()
  const as = me?.member?.id ?? null
  const changes = session.changes ?? null
  const read = useCallback((path: string) => api.sessionChangesDiff(session.id, path, as), [api, session.id, as])
  const view = useFileDiffs(changes, read)
  const [refreshing, setRefreshing] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const refreshingNow = useRef(false)
  const live = useRef(true)
  useEffect(() => {
    live.current = true
    return () => { live.current = false }
  }, [])

  const refresh = async () => {
    if (refreshingNow.current || session.state === 'ended' || store.getState().offline) return
    refreshingNow.current = true
    setRefreshing(true)
    setProblem(null)
    try {
      await store.sendSessionOp({ mutation_id: uuid4(), op: 'refresh_changes', args: { session_id: session.id } })
    } catch (err) {
      if (live.current) setProblem(errorMessage(err))
    } finally {
      refreshingNow.current = false
      if (live.current) setRefreshing(false)
    }
  }
  const files = changes?.files ?? []
  const added = files.reduce((sum, file) => sum + (file.additions ?? 0), 0)
  const removed = files.reduce((sum, file) => sum + (file.deletions ?? 0), 0)
  return (
    <div className="changes-pane">
      <div className="changes-head">
        <span className="changes-summary" title={changes ? `Against the session's base commit (${changes.base.slice(0, 9)})` : undefined}>
          {files.length > 0 && <span>{files.length} {files.length === 1 ? 'file' : 'files'}</span>}
          {files.length > 0 && <span className="changes-counts"><span className="changes-added">+{added}</span> <span className="changes-removed">−{removed}</span></span>}
        </span>
        <button type="button" className="button button-small" onClick={() => void refresh()} disabled={refreshing || session.state === 'ended' || offline} {...needsServer(offline)} aria-busy={refreshing}>Refresh</button>
      </div>
      {changes && <p className="changes-asof">As of <time dateTime={changes.at}>{exactTime(changes.at)}</time></p>}
      {problem !== null && <p className="changes-problem" role="alert">The changes could not be read again: {problem}</p>}
      {changes === null ? (
        <div className="changes-state"><p>There are no changes to show for this session.</p></div>
      ) : files.length === 0 ? (
        <div className="changes-state"><p>No changes.</p><p className="muted">The work tree holds nothing that the session's base commit does not.</p></div>
      ) : (
        <ChangedFiles files={files} view={view} />
      )}
      {changes?.truncated && <p className="changes-truncated">More files changed than are listed here.</p>}
    </div>
  )
}

export function ChangesPane({ session }: { session: Session | null }) {
  if (!session) return <div className="changes-pane"><div className="changes-state"><p>No session is open.</p><p className="muted">A session's changes show here.</p></div></div>
  return <SessionChanges key={session.id} session={session} />
}
